import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import {
  cancelPlanChangeSchedule,
  confirmBilling,
  createBillingPortalSession,
  fetchPaidCatalog,
  fetchServiceSubscription,
  terminateServiceSubscription,
  withdrawServiceCancellation,
  type OpenInvoiceSummary,
  type PaidCatalogResponse,
  type ServiceSubscriptionSummary,
} from "@/api/service-subscription";
import { apiErrorMessage } from "@/api/client";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SecureAccessSettings } from "@/features/app-bindings/SecureAccessSettings";
import { PaidPlanSelector, kintonePlanLabel } from "./PaidPlanSelector";
import { ServiceCancellationDialog, formatDay } from "./ServiceCancellationDialog";

export function ServiceSubscriptionPage() {
  const [subscription, setSubscription] = useState<ServiceSubscriptionSummary | null>(null);
  const [catalog, setCatalog] = useState<PaidCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const planCardRef = useRef<HTMLDivElement>(null);
  const portalNavigationPending = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [current, paidCatalog] = await Promise.all([
        fetchServiceSubscription(),
        fetchPaidCatalog(),
      ]);
      setSubscription(current ?? null);
      setCatalog(paidCatalog);
    } catch (cause) {
      setError(apiErrorMessage(cause, "サービス利用状態を取得できませんでした。"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted || !portalNavigationPending.current) return;
      portalNavigationPending.current = false;
      setBusy(false);
    }
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  async function handleCancelSchedule() {
    setBusy(true);
    setError(null);
    try {
      setSubscription(await cancelPlanChangeSchedule());
    } catch (cause) {
      setError(apiErrorMessage(cause, "予約変更を取り消せませんでした。"));
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmBilling(cancelCheckout = false) {
    setBusy(true);
    setError(null);
    try {
      await confirmBilling(cancelCheckout);
      await load();
    } catch (cause) {
      setError(apiErrorMessage(cause, "決済状態を確認できませんでした。"));
    } finally {
      setBusy(false);
    }
  }

  async function handleTerminate() {
    setBusy(true);
    setError(null);
    try {
      await terminateServiceSubscription();
      setCancelDialogOpen(false);
      await load();
    } catch (cause) {
      setError(apiErrorMessage(cause, "サービス利用を解約できませんでした。"));
      setCancelDialogOpen(false);
    } finally {
      setBusy(false);
    }
  }

  async function handleWithdrawCancellation() {
    setBusy(true);
    setError(null);
    try {
      setSubscription(await withdrawServiceCancellation());
    } catch (cause) {
      setError(apiErrorMessage(cause, "解約予定を取り消せませんでした。"));
    } finally {
      setBusy(false);
    }
  }

  async function handleOpenPortal() {
    if (busy || portalNavigationPending.current) return;
    setBusy(true);
    setError(null);
    try {
      const { portalUrl } = await createBillingPortalSession();
      portalNavigationPending.current = true;
      window.location.assign(portalUrl);
    } catch (cause) {
      portalNavigationPending.current = false;
      setError(apiErrorMessage(cause, "お支払い画面を開けませんでした。"));
      setBusy(false);
    }
  }

  function scrollToPlanCard() {
    setCancelDialogOpen(false);
    planCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const billing = subscription?.billingSubscriptionSummary ?? null;
  const paymentRequired = subscription?.commercialState === "payment_required";
  const recurringStopped = Boolean(subscription?.planKind === "paid" && billing && !billing.recurringBillingActive);
  // The API refuses plan changes while unpaid (restart only) or while a cancellation is scheduled (§4.2).
  const planChangeOpen = Boolean(
    subscription &&
    !subscription.checkoutPending &&
    !subscription.scheduledCancellationOn &&
    !(paymentRequired && !recurringStopped),
  );
  const currentGrade = catalog?.grades.find((entry) => entry.gradeCode === subscription?.paidGrade);
  const hasScheduledChange = Boolean(subscription?.scheduledGradeChange || billing?.scheduledBillingCycleChange);
  const byInvoice = billing?.collectionMethod === "send_invoice";
  const openInvoice = byInvoice ? billing?.openInvoice ?? null : null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">サービス利用・請求</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            この組織で利用する唯一の kintone サブドメインと請求状態を管理します。
          </p>
        </div>
        <Button variant="outline" onClick={() => void load()} disabled={loading || busy}>
          <RefreshCw className="mr-2 h-4 w-4" />再読込
        </Button>
      </div>

      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
      {loading ? <p className="text-sm text-muted-foreground">読み込み中…</p> : null}

      {subscription?.serviceSubscriptionState === "canceled" ? (
        <Card>
          <CardHeader>
            <CardTitle>解約が完了しました</CardTitle>
            <CardDescription>これまでのご利用ありがとうございました。</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>kintone の各アプリから EXTONE Drive のプラグインを削除してください。</li>
              <li>接続先ストレージに保存したファイルはそのまま残っています。必要なければストレージ側で削除してください。</li>
              <li>アカウントや組織の情報を消したい場合は、組織設定から削除できます。</li>
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {subscription?.serviceSubscriptionState === "active" && paymentRequired ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900">
          {recurringStopped ? (
            <>
              <p className="font-semibold">有料プランの定期課金が止まっているため、サービスを停止しています</p>
              <p className="mt-1">有料プランを再開すると、登録済みのアプリと設定のまま、すぐに使えるようになります。</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {subscription.billingAvailable ? <Button onClick={scrollToPlanCard} disabled={busy}>有料プランを再開する</Button> : <p>再開については問い合わせ窓口にご連絡ください。</p>}
              </div>
            </>
          ) : byInvoice ? (
            <>
              <p className="font-semibold">請求書のお支払いを確認できないため、サービスを停止しています</p>
              <p className="mt-1">未払いの請求書をお支払いください。お支払いを確認できると、すぐに使えるようになります。</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {openInvoice?.invoiceUrl ? <InvoiceLink url={openInvoice.invoiceUrl} /> : null}
                <Button variant="outline" onClick={() => void handleConfirmBilling()} disabled={busy}>支払後に状態を再確認</Button>
              </div>
            </>
          ) : (
            <>
              <p className="font-semibold">お支払いを確認できないため、サービスを停止しています</p>
              <p className="mt-1">カードの有効期限切れや残高不足、本人認証（3Dセキュア）が済んでいないことが考えられます。お支払い方法を更新するか、未払いの請求をお支払いください。支払が確認できると、すぐに使えるようになります。</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {billing?.billingPortalAvailable ? <Button onClick={() => void handleOpenPortal()} disabled={busy}>お支払い方法を更新</Button> : null}
                <Button variant="outline" onClick={() => void handleConfirmBilling()} disabled={busy}>支払後に状態を再確認</Button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {subscription?.serviceSubscriptionState === "active" && subscription.commercialState === "expired" ? (
        <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          <p className="font-semibold">お試し期間が終了したため、サービスを停止しています</p>
          <p className="mt-1">有料プランを申し込むと、登録済みのアプリと設定のまま、すぐに使えるようになります。</p>
          {subscription.billingAvailable ? <Button className="mt-3" onClick={scrollToPlanCard} disabled={busy}>有料プランを申し込む</Button> : null}
        </div>
      ) : null}

      {subscription?.serviceSubscriptionState === "active" && subscription.scheduledCancellationOn ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          <p className="font-semibold">{formatDay(subscription.scheduledCancellationOn)}で解約します</p>
          <p className="mt-1">この日までは今のプランで使えます。翌日にサービスが止まります。プランの変更は、解約予定を取り消してから行えます。</p>
          <Button className="mt-3" variant="outline" onClick={() => void handleWithdrawCancellation()} disabled={busy}>解約を取り消す</Button>
        </div>
      ) : null}

      {!loading && !subscription ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">組織登録時のサービス利用作成に失敗しています。再読み込みしても解消しない場合は管理者へ連絡してください。</div>
      ) : null}

      {subscription ? (
        <>
          {subscription.checkoutPending ? <Card>
            <CardHeader><CardTitle>決済を確認中です</CardTitle><CardDescription>決済を中断する場合は取り消してください。支払いが完了している場合はその結果を反映します。</CardDescription></CardHeader>
            <CardContent className="flex gap-2">
              <Button disabled={busy} onClick={() => void handleConfirmBilling()}>決済状態を確認</Button>
              <Button variant="outline" disabled={busy} onClick={() => void handleConfirmBilling(true)}>未完了の決済を取り消す</Button>
            </CardContent>
          </Card> : null}

          {subscription.serviceSubscriptionState === "active" && openInvoice ? (
            <OpenInvoiceCard invoice={openInvoice} stopped={paymentRequired} konbiniMaxAmountWithTax={catalog?.invoicePayment.konbiniMaxAmountWithTax ?? null} />
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>{subscription.kintoneSubdomain ? `kintoneサブドメイン: ${subscription.kintoneSubdomain}` : "kintoneサブドメイン未登録"}</CardTitle>
              <CardDescription>{subscription.kintoneSubdomain ? "kintone環境の変更は「kintoneアプリ」画面から診断と確認を行って実施できます。解約後の再有効化はできません。" : "kintoneアプリ画面からサブドメインを入力し、セットアップを開始してください。"}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Metric label="サービス状態" value={<StatusBadge state={subscription.serviceSubscriptionState} />} />
              <Metric label="プラン" value={subscription.planKind === "trial" ? (subscription.releasePhase === "beta" ? "お試しプラン（ベータ版）" : "お試しプラン") : "有料プラン"} />
              <Metric label="利用状況" value={<StatusBadge state={subscription.commercialState} />} />
              {subscription.resourceReleaseState !== "not_required" ? <Metric label="接続環境の状態" value={<StatusBadge state={subscription.resourceReleaseState} />} /> : null}
              <Metric label="登録済みアプリ" value={`${subscription.registeredKintoneAppCount}${subscription.appLimit ? ` / ${subscription.appLimit}` : ""}`} />
              {subscription.planKind === "trial" && subscription.effectiveTrialExpiresAt ? <Metric label="試用期限" value={formatDate(subscription.effectiveTrialExpiresAt)} /> : null}
              {currentGrade ? <Metric label="kintoneコース（自己申告）" value={kintonePlanLabel(currentGrade.kintonePlan)} /> : null}
              {subscription.planKind === "paid" ? <Metric label="契約アプリ数" value={`${subscription.appLimit}アプリまで`} /> : null}
              {billing ? <Metric label="請求方法" value={byInvoice ? "請求書払い（銀行振込・コンビニ）" : "カード"} /> : null}
              <Metric label="請求周期" value={subscription.billingSubscriptionSummary ? (subscription.billingSubscriptionSummary.billingCycle === "monthly" ? "月払い" : "年払い") : "—"} />
              <Metric label="請求期限" value={subscription.billingSubscriptionSummary?.expiresOn ?? "—"} />
              {subscription.scheduledCancellationOn ? <Metric label="解約予定（最終利用日）" value={formatDay(subscription.scheduledCancellationOn)} /> : null}
              {hasScheduledChange ? <Metric label="変更予定" value={scheduledChangeText(subscription, catalog)} /> : null}
            </CardContent>
          </Card>

          {subscription.serviceSubscriptionState === "active" &&
           subscription.kintoneConnectionState === "registered" &&
           subscription.kintoneSubdomain ? <SecureAccessSettings /> : null}

          {subscription.serviceSubscriptionState === "active" && !subscription.billingAvailable ? <Card><CardHeader><CardTitle>有料プランは準備中です</CardTitle><CardDescription>{subscription.planKind === "trial" ? "ベータ版の提供期間中は無料です。正式公開後も30日間お試しいただけます。自動で課金されることはありません。" : "新しい有料プランの申込みは停止しています。既存の契約条件は変更されません。"}</CardDescription></CardHeader></Card> : null}

          {subscription.serviceSubscriptionState === "active" && subscription.billingAvailable && planChangeOpen && !hasScheduledChange ? (
            <Card ref={planCardRef}>
              <CardHeader>
                <CardTitle>有料プラン</CardTitle>
                <CardDescription>{subscription.planKind === "paid" ? "アプリ数や請求周期を変更できます。" : "お使いのアプリ数に合わせてプランを選べます。"}料金は消費税10%を含めて表示します。</CardDescription>
              </CardHeader>
              <CardContent>
                {catalog ? <PaidPlanSelector subscription={subscription} catalog={catalog} onChanged={load} /> : null}
              </CardContent>
            </Card>
          ) : null}

          {subscription.serviceSubscriptionState === "active" && hasScheduledChange ? (
            <Card>
              <CardHeader>
                <CardTitle>予約中のプラン変更</CardTitle>
                <CardDescription>{scheduledChangeText(subscription, catalog)}それまでは今のプランのままです。別のプランに変える場合は、予約を取り消してから選び直してください。</CardDescription>
              </CardHeader>
              <CardContent><Button variant="outline" onClick={() => void handleCancelSchedule()} disabled={busy}>予約を取消</Button></CardContent>
            </Card>
          ) : null}

          {billing?.billingPortalAvailable ? (
            <Card>
              <CardHeader>
                <CardTitle>請求書とお支払い方法</CardTitle>
                <CardDescription>{byInvoice ? "これまでの請求書と領収書は、決済サービス（Stripe）の画面で確認できます。" : "請求書・領収書の確認、お支払い方法の変更は、決済サービス（Stripe）の画面で行います。"}</CardDescription>
              </CardHeader>
              <CardContent><Button variant="outline" onClick={() => void handleOpenPortal()} disabled={busy}>{byInvoice ? "請求書の一覧を開く" : "請求書・お支払い方法を開く"}</Button></CardContent>
            </Card>
          ) : null}

          {subscription.serviceSubscriptionState === "active" && !subscription.scheduledCancellationOn ? (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle>サービス利用の解約</CardTitle>
                <CardDescription>解約後は、この組織でのサービス利用を再開できません。解約前に、影響と代わりの方法を確認できます。</CardDescription>
              </CardHeader>
              <CardContent><Button variant="destructive" onClick={() => setCancelDialogOpen(true)} disabled={busy}>解約する</Button></CardContent>
            </Card>
          ) : null}

          <ServiceCancellationDialog
            open={cancelDialogOpen}
            subscription={subscription}
            busy={busy}
            onClose={() => setCancelDialogOpen(false)}
            onConfirm={handleTerminate}
            onChoosePlanChange={subscription.billingAvailable && planChangeOpen ? scrollToPlanCard : undefined}
          />
        </>
      ) : null}
    </div>
  );
}

/** D-7: the oldest unpaid invoice of an invoice-payment contract, with the D10 stop and auto-cancel dates. */
function OpenInvoiceCard({ invoice, stopped, konbiniMaxAmountWithTax }: {
  invoice: OpenInvoiceSummary;
  stopped: boolean;
  konbiniMaxAmountWithTax: number | null;
}) {
  const overdue = todayIso() > invoice.dueOn;
  return (
    <Card className={stopped ? "border-red-300" : overdue ? "border-amber-300" : undefined}>
      <CardHeader>
        <CardTitle>未払いの請求書</CardTitle>
        <CardDescription>
          {stopped
            ? `サービスを停止しています。${formatDay(invoice.autoCancelOn)}までにお支払いがないときは、契約を自動で解約します。解約後も請求額のお支払いは必要です。データは削除しません。`
            : overdue
              ? `支払期限を過ぎています。${formatDay(invoice.suspendOn)}までにお支払いを確認できないときは、サービスを停止します。`
              : `支払期限までにお支払いください。期限を過ぎて${formatDay(invoice.suspendOn)}になってもお支払いがないときは、サービスを停止します。`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid gap-4 sm:grid-cols-2">
          <Metric label="請求額（税込）" value={yen(invoice.amount)} />
          <Metric label="支払期限" value={formatDay(invoice.dueOn)} />
        </div>
        <p className="text-muted-foreground">
          お支払いは銀行振込かコンビニ払いです。振込手数料はお客様のご負担です。
          {konbiniMaxAmountWithTax !== null && invoice.amount > konbiniMaxAmountWithTax ? "この請求額はコンビニ払いの上限を超えるため、銀行振込でお支払いください。" : ""}
        </p>
        {invoice.invoiceUrl
          ? <InvoiceLink url={invoice.invoiceUrl} />
          : <p className="text-muted-foreground">請求書を準備しています。しばらくしてから再読込してください。</p>}
      </CardContent>
    </Card>
  );
}

function InvoiceLink({ url }: { url: string }) {
  return (
    <Button asChild>
      <a href={url} target="_blank" rel="noreferrer">請求書を開く（振込先・コンビニ払い）</a>
    </Button>
  );
}

function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function yen(value: number) {
  return new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(value);
}

function Metric({ label, value }: { label: string; value: ReactNode }) {
  return <div><p className="text-xs text-muted-foreground">{label}</p><div className="mt-1 font-medium">{value}</div></div>;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ja-JP", { dateStyle: "medium" }).format(new Date(value));
}

/** "2026年10月19日から20アプリ・月払いに変更します。" (C4) */
function scheduledChangeText(subscription: ServiceSubscriptionSummary, catalog: PaidCatalogResponse | null) {
  const billing = subscription.billingSubscriptionSummary;
  const on = subscription.scheduledEffectiveOn ?? billing?.scheduledCycleEffectiveOn ?? null;
  const grade = catalog?.grades.find((entry) => entry.gradeCode === (subscription.scheduledGradeChange ?? subscription.paidGrade));
  const cycle = billing?.scheduledBillingCycleChange ?? billing?.billingCycle ?? null;
  const target = [grade ? kintonePlanLabel(grade.kintonePlan) : null, grade ? `${grade.appLimit}アプリ` : null, cycle ? (cycle === "monthly" ? "月払い" : "年払い") : null]
    .filter(Boolean).join("・");
  return `${on ? `${formatDay(on)}から` : "次回更新時から"}${target || "新しいプラン"}に変更します。`;
}
